import { DEFAULT_TIMEZONE } from "@dream/config/constants";
import {
  allDayRange,
  isGoogleOverlayNoise,
  localDateKey,
  parseDateOnlyKey,
  parseOccurrenceId,
  type ResolvedOccurrence,
} from "@dream/domain";
import type { EventInput } from "@fullcalendar/core";
import { type ChipPerson, labelFromEmail, parseGoogleAttendees } from "../../lib/people.ts";
import { fullCalendarMarkerToIso } from "./fullcalendar-date.ts";

/**
 * Adapter do FullCalendar.
 *
 * Única fronteira entre o modelo de domínio e o plugin. O briefing exige "criar
 * um adapter próprio para impedir que regras de negócio dependam do
 * FullCalendar" — nenhum módulo de `packages/domain` importa `@fullcalendar/*`,
 * e nada fora deste diretório manipula `EventInput`.
 *
 * A recorrência **não** é delegada ao plugin de RRULE. A expansão acontece no
 * servidor, com overrides e exceções aplicados, e o calendário recebe
 * ocorrências já resolvidas. Passar a RRULE para o cliente faria as duas pontas
 * expandirem de formas potencialmente divergentes, e o override individual
 * ficaria impossível de representar.
 */

export interface DreamEventProps {
  occurrenceId: string;
  deliverableId: string;
  occurrenceStart: string;
  status: ResolvedOccurrence["status"];
  priority: ResolvedOccurrence["priority"];
  clientId: string;
  projectId: string | null;
  typeId: string;
  assigneeId: string | null;
  isRecurring: boolean;
  isOverride: boolean;
  isLate: boolean;
  hasPublish: boolean;
  visibleToClient: boolean;
  version: number;
  timezone: string;
  description: string | null;
  linkCount: number;
  clientName: string | null;
  projectName: string | null;
  typeLabel: string | null;
  typeIcon: string | null;
  typeSlug: string | null;
  assigneeName: string | null;
  people: ChipPerson[];
  /** Instante real — o FullCalendar achata `start` e o mês pinado vira meia-noite. */
  originalStartsAt: string;
  originalAllDay: boolean;
  sortMs: number;
}

export interface TypeMeta {
  id: string;
  label: string;
  color: string;
  icon: string | null;
  slug: string | null;
}

export interface ClientMeta {
  id: string;
  name: string;
  color: string;
}

export interface ProjectMeta {
  id: string;
  name: string;
}

/**
 * Ocorrência de domínio → evento do FullCalendar.
 *
 * O `id` é o `occurrenceId` composto (`deliverableId:instanteISO`), não o ID da
 * entrega: numa série, várias ocorrências compartilham a entrega, e usar o ID da
 * entrega faria o FullCalendar tratá-las como um único evento.
 */
export interface MappingContext {
  types: Map<string, TypeMeta>;
  clients: Map<string, ClientMeta>;
  projects?: Map<string, ProjectMeta>;
  team?: Map<string, TeamMemberMeta>;
  colorBy: "type" | "client";
  /**
   * `false` no portal do cliente.
   *
   * Precisa chegar até aqui porque, no FullCalendar, o `editable` **por
   * evento** tem precedência sobre o `editable` do calendário. Marcar o evento
   * como editável reativaria o arraste mesmo com o calendário declarado não
   * interativo — e o portal precisa ser somente leitura também no gesto, não
   * apenas no servidor.
   */
  interactive: boolean;
}

export function toEventInput(occurrence: ResolvedOccurrence, context: MappingContext): EventInput {
  const type = context.types.get(occurrence.typeId);
  const client = context.clients.get(occurrence.clientId);
  const project =
    occurrence.projectId === null ? undefined : context.projects?.get(occurrence.projectId);

  const color =
    context.colorBy === "client"
      ? (client?.color ?? "#0074FF")
      : (type?.color ?? client?.color ?? "#0074FF");

  const props: DreamEventProps = {
    occurrenceId: occurrence.occurrenceId,
    deliverableId: occurrence.deliverableId,
    // Serializado: `extendedProps` atravessa o FullCalendar, que pode clonar
    // o objeto. Uma `Date` viraria string em alguns caminhos e permaneceria
    // `Date` em outros, então o tipo é fixado como ISO desde a origem.
    occurrenceStart: occurrence.occurrenceStart.toISOString(),
    status: occurrence.status,
    priority: occurrence.priority,
    clientId: occurrence.clientId,
    projectId: occurrence.projectId,
    typeId: occurrence.typeId,
    assigneeId: occurrence.assigneeId,
    isRecurring: occurrence.isRecurring,
    isOverride: occurrence.isOverride,
    isLate: occurrence.isLate,
    hasPublish: occurrence.publishAt !== null,
    visibleToClient: occurrence.visibleToClient,
    version: occurrence.version,
    timezone: occurrence.timezone,
    description: occurrence.description,
    linkCount: occurrence.links.length,
    clientName: client?.name ?? null,
    projectName: project?.name ?? null,
    typeLabel: type?.label ?? null,
    typeIcon: type?.icon ?? null,
    typeSlug: type?.slug ?? null,
    assigneeName:
      occurrence.assigneeId === null
        ? null
        : (context.team?.get(occurrence.assigneeId)?.name ?? null),
    people: peopleFromAssignee(occurrence.assigneeId, context.team),
    originalStartsAt: occurrence.startsAt.toISOString(),
    originalAllDay: occurrence.isAllDay,
    sortMs: occurrence.startsAt.getTime(),
  };

  return {
    id: occurrence.occurrenceId,
    title: occurrence.title,
    start: occurrence.isAllDay
      ? localDateKey(occurrence.startsAt, occurrence.timezone)
      : occurrence.startsAt.toISOString(),
    end: occurrence.isAllDay
      ? localDateKey(occurrence.endsAt, occurrence.timezone)
      : occurrence.endsAt.toISOString(),
    allDay: occurrence.isAllDay,
    backgroundColor: color,
    borderColor: color,
    // Só editável quando o calendário é interativo E a entrega não está
    // cancelada — mover algo cancelado não tem significado operacional.
    editable: context.interactive && occurrence.status !== "cancelled",
    extendedProps: props,
    classNames: [
      "dream-event",
      `dream-event--${occurrence.status}`,
      occurrence.isLate ? "dream-event--late" : "",
      occurrence.isAllDay ? "dream-event--allday" : "",
    ].filter((c) => c !== ""),
  };
}

export function toEventInputs(
  occurrences: ResolvedOccurrence[],
  context: MappingContext,
): EventInput[] {
  return occurrences.map((o) => toEventInput(o, context));
}

/**
 * Evento da Google Agenda, só no calendário interno.
 *
 * Não é entrega: não arrasta, não abre o diálogo e o cliente não vê. O `id`
 * usa o prefixo `google:` para nunca colidir com um `occurrenceId`.
 */
export interface GoogleOverlayEvent {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  isAllDay: boolean;
  htmlLink: string | null;
  calendarName: string;
  color: string;
  foreground: string | null;
  colorId: string | null;
  location: string | null;
  hangoutLink: string | null;
  transparency: string | null;
  eventType: string | null;
  attendees: string[];
  attendeeDetails?: Array<{ email: string; displayName: string | null }>;
  calendarEmail?: string;
}

export interface TeamMemberMeta {
  id: string;
  name: string;
  email?: string | null;
  image?: string | null;
  avatarColor?: string | null;
}

export interface GoogleEventProps {
  source: "google";
  htmlLink: string | null;
  calendarName: string;
  location: string | null;
  hangoutLink: string | null;
  attendees: string[];
  people?: ChipPerson[];
  transparency: string | null;
  eventType: string | null;
  colorId: string | null;
  foreground: string | null;
  /** Instante real da Agenda — o mês pode piná-lo como dia inteiro na grade. */
  originalStartsAt: string;
  originalEndsAt: string;
  originalAllDay: boolean;
  sortMs: number;
}

export function isGoogleOverlay(extendedProps: Record<string, unknown>): boolean {
  return extendedProps.source === "google";
}

const WRITEBACK_TITLE_SEP = " · ";
/** Clock skew Google↔Dream e o mesmo compromisso em duas agendas. */
const OVERLAP_MS = 15 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

function normalizeTitle(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function overlayMinuteKey(title: string, start: number, end: number, allDay: boolean): string {
  return `${normalizeTitle(title)}|${Math.round(start / MINUTE_MS)}|${Math.round(end / MINUTE_MS)}|${allDay ? 1 : 0}`;
}

function overlayCivilDay(startsAt: string): string {
  return localDateKey(new Date(startsAt), DEFAULT_TIMEZONE);
}

export function googleTitleMatchesDeliverable(
  googleTitle: string,
  deliverableTitle: string,
  clientName: string | null,
): boolean {
  const google = normalizeTitle(googleTitle);
  const deliverable = normalizeTitle(deliverableTitle);
  if (google === "" || deliverable === "") return false;
  if (google === deliverable) return true;
  const client = clientName === null ? "" : normalizeTitle(clientName);
  if (client !== "" && google === `${client}${WRITEBACK_TITLE_SEP}${deliverable}`) return true;
  if (google.endsWith(`${WRITEBACK_TITLE_SEP}${deliverable}`)) return true;
  if (deliverable.length >= 6 && google.includes(deliverable)) return true;
  if (client !== "" && google.startsWith(client) && google.includes(deliverable)) return true;
  return false;
}

function isDreamWritebackCopy(
  event: GoogleOverlayEvent,
  start: number,
  end: number,
  occurrence: {
    title: string;
    startsAt: Date;
    endsAt?: Date;
    isAllDay: boolean;
    status: string;
    clientId: string;
    timezone?: string;
  },
  clientName: string | null,
): boolean {
  if (occurrence.status === "cancelled") return false;
  if (!googleTitleMatchesDeliverable(event.title, occurrence.title, clientName)) return false;

  const occStart = occurrence.startsAt.getTime();
  const occEnd = occurrence.endsAt?.getTime() ?? occStart;
  if (Math.abs(start - occStart) <= OVERLAP_MS) return true;
  const overlap = Math.min(end, occEnd) - Math.max(start, occStart);
  if (overlap > 0) return true;

  const zone = occurrence.timezone ?? DEFAULT_TIMEZONE;
  const sameCivil = overlayCivilDay(event.startsAt) === localDateKey(occurrence.startsAt, zone);
  return sameCivil && (event.isAllDay || occurrence.isAllDay);
}

function isNearDuplicateOverlay(a: GoogleOverlayEvent, b: GoogleOverlayEvent): boolean {
  if (normalizeTitle(a.title) !== normalizeTitle(b.title)) return false;
  if (overlayCivilDay(a.startsAt) !== overlayCivilDay(b.startsAt)) return false;
  return Math.abs(Date.parse(a.startsAt) - Date.parse(b.startsAt)) <= OVERLAP_MS;
}

/** Overlay estável: horário crescente, depois título. */
export function sortGoogleOverlay(events: GoogleOverlayEvent[]): GoogleOverlayEvent[] {
  return [...events].sort((a, b) => {
    const delta = Date.parse(a.startsAt) - Date.parse(b.startsAt);
    if (delta !== 0) return delta;
    const title = a.title.localeCompare(b.title, "pt-BR");
    if (title !== 0) return title;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Tira do overlay a cópia Google da entrega do Dream (write-back) e o mesmo
 * compromisso repetido em duas agendas da pessoa.
 */
export function withoutDreamWritebackOverlay(
  events: GoogleOverlayEvent[],
  occurrences: Array<{
    title: string;
    startsAt: Date;
    endsAt?: Date;
    isAllDay: boolean;
    status: string;
    clientId: string;
    timezone?: string;
  }>,
  clients: Array<{ id: string; name: string }>,
): GoogleOverlayEvent[] {
  const clientName = new Map(clients.map((client) => [client.id, client.name]));
  const seen = new Set<string>();
  const overlay: GoogleOverlayEvent[] = [];

  for (const event of events) {
    if (isGoogleOverlayNoise(event)) continue;
    const start = Date.parse(event.startsAt);
    const end = Date.parse(event.endsAt);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    const duplicateKey = overlayMinuteKey(event.title, start, end, event.isAllDay);
    if (seen.has(duplicateKey)) continue;

    const writeback = occurrences.some((occurrence) =>
      isDreamWritebackCopy(
        event,
        start,
        end,
        occurrence,
        clientName.get(occurrence.clientId) ?? null,
      ),
    );
    if (writeback) continue;

    seen.add(duplicateKey);
    overlay.push(event);
  }

  const ordered = sortGoogleOverlay(overlay);
  const unique: GoogleOverlayEvent[] = [];
  for (const event of ordered) {
    if (unique.some((prev) => isNearDuplicateOverlay(prev, event))) continue;
    unique.push(event);
  }
  return unique;
}

function readSortMs(input: {
  sortMs?: unknown;
  extendedProps?: Record<string, unknown>;
}): number | null {
  const direct = input.sortMs;
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const nested = input.extendedProps?.sortMs;
  if (typeof nested === "number" && Number.isFinite(nested)) return nested;
  return null;
}

export function calendarEventSortMs(input: {
  start: Date | string | number | null;
  allDay: boolean | number;
  sortMs?: unknown;
  originalStartsAt?: unknown;
  extendedProps?: Record<string, unknown>;
}): number {
  const stamped = readSortMs(input);
  if (stamped !== null) return stamped;
  const original =
    typeof input.originalStartsAt === "string"
      ? input.originalStartsAt
      : typeof input.extendedProps?.originalStartsAt === "string"
        ? input.extendedProps.originalStartsAt
        : undefined;
  if (original !== undefined && original.length > 0) {
    const instant = Date.parse(original);
    if (Number.isFinite(instant)) return instant;
  }
  if (input.start === null) return 0;
  if (typeof input.start === "number") {
    return Number.isFinite(input.start) ? input.start : 0;
  }
  if (input.start instanceof Date) return input.start.getTime();
  if (typeof input.start !== "string") return 0;
  const instant = Date.parse(
    input.start.length === 10 ? `${input.start}T00:00:00.000-03:00` : input.start,
  );
  return Number.isFinite(instant) ? instant : 0;
}

function isTrueAllDayEvent(input: {
  allDay: boolean | number;
  source?: unknown;
  originalAllDay?: unknown;
  extendedProps?: Record<string, unknown>;
}): boolean {
  const source = input.extendedProps?.source ?? input.source;
  const originalAllDay = input.extendedProps?.originalAllDay ?? input.originalAllDay;
  if (source === "google") return originalAllDay === true;
  return Boolean(input.allDay);
}

/**
 * Ordem cronológica na célula: o relógio real (`sortMs` / `originalStartsAt`),
 * não o pin do mês à meia-noite. Empate: dia inteiro, entrega Dream, título.
 *
 * O FullCalendar **não** chama `eventOrder` com `EventApi`. Ele achata
 * `extendedProps` e troca `start` pelo `valueOf()` do intervalo — número em
 * ms. Sem `sortMs` nesse objeto, o mês pinado empata tudo na meia-noite e a
 * célula cai na ordem do título.
 */
export function compareCalendarEvents(
  a: {
    title: string;
    start: Date | string | number | null;
    allDay: boolean | number;
    source?: unknown;
    sortMs?: unknown;
    originalStartsAt?: unknown;
    originalAllDay?: unknown;
    extendedProps?: Record<string, unknown>;
  },
  b: {
    title: string;
    start: Date | string | number | null;
    allDay: boolean | number;
    source?: unknown;
    sortMs?: unknown;
    originalStartsAt?: unknown;
    originalAllDay?: unknown;
    extendedProps?: Record<string, unknown>;
  },
): number {
  const delta = calendarEventSortMs(a) - calendarEventSortMs(b);
  if (delta !== 0) return delta;
  const allDayA = isTrueAllDayEvent(a);
  const allDayB = isTrueAllDayEvent(b);
  if (allDayA !== allDayB) return allDayA ? -1 : 1;
  const googleA = (a.extendedProps?.source ?? a.source) === "google";
  const googleB = (b.extendedProps?.source ?? b.source) === "google";
  if (googleA !== googleB) return googleA ? 1 : -1;
  return a.title.localeCompare(b.title, "pt-BR");
}

export function compareCalendarEventInputs(a: EventInput, b: EventInput): number {
  const startOf = (value: EventInput["start"]): Date | string | null => {
    if (value instanceof Date || typeof value === "string") return value;
    return null;
  };
  return compareCalendarEvents(
    {
      title: typeof a.title === "string" ? a.title : "",
      start: startOf(a.start),
      allDay: a.allDay === true,
      extendedProps: a.extendedProps as Record<string, unknown> | undefined,
    },
    {
      title: typeof b.title === "string" ? b.title : "",
      start: startOf(b.start),
      allDay: b.allDay === true,
      extendedProps: b.extendedProps as Record<string, unknown> | undefined,
    },
  );
}

/**
 * Overlay visível: rotina diária/semanal/mensal entra. Só some o chrome da
 * Agenda (working location, e-mail, aniversário).
 */
export function visibleGoogleOverlay(events: GoogleOverlayEvent[]): GoogleOverlayEvent[] {
  return events.filter((event) => !isGoogleOverlayNoise(event));
}

/**
 * Intervalo de dia inteiro (fim exclusivo) no dia civil de `startsAt`.
 *
 * No mês, evento com horário vira chip desse dia. Sem isso o Idle 21:00
 * (00:00 UTC do dia seguinte) escorre para sábado, e o dayGrid posiciona o
 * bloco pela hora em vez da data civil da Agenda.
 */
function civilDayExclusiveKeys(startsAt: string): { start: string; end: string } | null {
  const startKey = localDateKey(new Date(startsAt), DEFAULT_TIMEZONE);
  const parts = parseDateOnlyKey(startKey);
  if (parts === null) return null;
  const range = allDayRange(parts, DEFAULT_TIMEZONE, 1);
  return {
    start: localDateKey(range.startsAt, DEFAULT_TIMEZONE),
    end: localDateKey(range.endsAt, DEFAULT_TIMEZONE),
  };
}

function personFromMember(member: TeamMemberMeta): ChipPerson {
  return {
    id: member.id,
    name: member.name,
    imageUrl: member.image ?? null,
    color: member.avatarColor ?? null,
  };
}

function peopleFromAssignee(
  assigneeId: string | null,
  team: Map<string, TeamMemberMeta> | undefined,
): ChipPerson[] {
  if (assigneeId === null || team === undefined) return [];
  const member = team.get(assigneeId);
  return member === undefined ? [] : [personFromMember(member)];
}

function peopleFromAttendees(
  emails: unknown,
  team: Map<string, TeamMemberMeta> | undefined,
): ChipPerson[] {
  const byEmail = new Map<string, TeamMemberMeta>();
  if (team !== undefined) {
    for (const member of team.values()) {
      const email = member.email?.trim().toLowerCase() ?? "";
      if (email !== "") byEmail.set(email, member);
    }
  }
  const people: ChipPerson[] = [];
  const seen = new Set<string>();
  for (const attendee of parseGoogleAttendees(emails)) {
    if (seen.has(attendee.email)) continue;
    seen.add(attendee.email);
    const member = byEmail.get(attendee.email);
    if (member !== undefined) {
      people.push(personFromMember(member));
      continue;
    }
    people.push({
      id: attendee.email,
      name: attendee.displayName ?? labelFromEmail(attendee.email),
      imageUrl: null,
      color: null,
    });
  }
  return people;
}

function peopleFromGoogleEvent(
  event: GoogleOverlayEvent,
  team: Map<string, TeamMemberMeta> | undefined,
): ChipPerson[] {
  const fromAttendees = peopleFromAttendees(event.attendeeDetails ?? event.attendees, team);
  if (fromAttendees.length > 0) return fromAttendees;
  const ownerEmail = (event.calendarEmail ?? event.calendarName).trim().toLowerCase();
  if (!ownerEmail.includes("@") || team === undefined) return [];
  for (const member of team.values()) {
    if ((member.email?.trim().toLowerCase() ?? "") === ownerEmail) {
      return [personFromMember(member)];
    }
  }
  return [];
}

export function toGoogleEventInputs(
  events: GoogleOverlayEvent[],
  options?: { pinToCivilDay?: boolean; team?: Map<string, TeamMemberMeta> },
): EventInput[] {
  const pinToCivilDay = options?.pinToCivilDay === true;
  return events.map((event) => {
    let start: string;
    let end: string;
    let allDay = event.isAllDay;
    if (event.isAllDay) {
      start = localDateKey(new Date(event.startsAt), DEFAULT_TIMEZONE);
      end = localDateKey(new Date(event.endsAt), DEFAULT_TIMEZONE);
    } else if (pinToCivilDay) {
      const civil = civilDayExclusiveKeys(event.startsAt);
      if (civil === null) {
        start = event.startsAt;
        end = event.endsAt;
      } else {
        start = civil.start;
        end = civil.end;
        allDay = true;
      }
    } else {
      start = event.startsAt;
      end = event.endsAt;
    }

    return {
      id: `google:${event.id}`,
      title: event.title,
      start,
      end,
      allDay,
      backgroundColor: event.color,
      borderColor: event.color,
      textColor: event.foreground ?? undefined,
      editable: false,
      startEditable: false,
      durationEditable: false,
      classNames: [
        "dream-event",
        "dream-event--google",
        event.transparency === "transparent" ? "dream-event--google-free" : "",
        event.hangoutLink !== null && event.hangoutLink !== "" ? "dream-event--google-meet" : "",
      ].filter((name) => name !== ""),
      extendedProps: {
        source: "google",
        htmlLink: event.htmlLink,
        calendarName: event.calendarName,
        location: event.location,
        hangoutLink: event.hangoutLink,
        attendees: event.attendees,
        people: peopleFromGoogleEvent(event, options?.team),
        transparency: event.transparency,
        eventType: event.eventType,
        colorId: event.colorId,
        foreground: event.foreground,
        originalStartsAt: event.startsAt,
        originalEndsAt: event.endsAt,
        originalAllDay: event.isAllDay,
        sortMs: Date.parse(event.startsAt),
      } satisfies GoogleEventProps,
    };
  });
}

/**
 * Comandos que o adapter emite. O calendário nunca chama a API direto.
 *
 * `revert` é a função do próprio FullCalendar que desfaz o arraste na tela.
 * Ela viaja no comando para que o chamador possa fazer o rollback visual
 * exigido pelo briefing quando a persistência falha ou dá conflito de versão —
 * sem isso, a UI ficaria mostrando uma data que o servidor recusou.
 */
export type CalendarCommand =
  | {
      kind: "move";
      deliverableId: string;
      version: number;
      startsAt: string;
      endsAt: string;
      /** Presente quando a ocorrência pertence a uma série. */
      occurrenceStart: string | undefined;
      allDay: boolean;
      revert?: () => void;
    }
  | {
      kind: "resize";
      deliverableId: string;
      version: number;
      endsAt: string;
      occurrenceStart: string | undefined;
      revert?: () => void;
    }
  | { kind: "open"; deliverableId: string; occurrenceStart: string; title?: string }
  | {
      kind: "context";
      deliverableId: string;
      occurrenceStart: string;
      title: string;
      version: number;
      typeId: string;
      color: string;
      startsAt: string;
      endsAt: string;
      allDay: boolean;
      x: number;
      y: number;
    }
  | { kind: "createRange"; startsAt: string; endsAt: string; allDay: boolean };

/**
 * Extrai os `extendedProps` de um evento do FullCalendar.
 *
 * O plugin tipa `extendedProps` como `Dictionary`, então esta é a única
 * conversão insegura do adapter — isolada aqui, e validada antes de usar.
 */
export function readProps(extendedProps: Record<string, unknown>): DreamEventProps | null {
  const candidate = extendedProps as unknown as Partial<DreamEventProps>;
  if (
    typeof candidate.deliverableId !== "string" ||
    typeof candidate.version !== "number" ||
    typeof candidate.occurrenceStart !== "string"
  ) {
    return null;
  }
  return candidate as DreamEventProps;
}

/** `eventDrop` do FullCalendar → comando de domínio. */
export type MoveCommand = Extract<CalendarCommand, { kind: "move" }>;
export type ResizeCommand = Extract<CalendarCommand, { kind: "resize" }>;
export type OpenCommand = Extract<CalendarCommand, { kind: "open" }>;

export function dropToCommand(input: {
  eventId: string;
  extendedProps: Record<string, unknown>;
  start: Date | null;
  end: Date | null;
  allDay: boolean;
}): MoveCommand | null {
  if (isGoogleOverlay(input.extendedProps)) return null;
  const props = readProps(input.extendedProps);
  if (props === null || input.start === null) return null;

  // Sem `end`, o FullCalendar considera o evento de duração padrão da view. O
  // servidor recalcula a duração original, então mandamos o início e deixamos
  // `endsAt` como o início — a regra de domínio corrige.
  const zone = props.timezone;
  const endsAt = input.end ?? input.start;

  return {
    kind: "move",
    deliverableId: props.deliverableId,
    version: props.version,
    startsAt: fullCalendarMarkerToIso(input.start, zone),
    endsAt: fullCalendarMarkerToIso(endsAt, zone),
    occurrenceStart: props.isRecurring ? props.occurrenceStart : undefined,
    allDay: input.allDay,
  };
}

export function resizeToCommand(input: {
  extendedProps: Record<string, unknown>;
  end: Date | null;
}): ResizeCommand | null {
  if (isGoogleOverlay(input.extendedProps)) return null;
  const props = readProps(input.extendedProps);
  if (props === null || input.end === null) return null;

  return {
    kind: "resize",
    deliverableId: props.deliverableId,
    version: props.version,
    endsAt: fullCalendarMarkerToIso(input.end, props.timezone),
    occurrenceStart: props.isRecurring ? props.occurrenceStart : undefined,
  };
}

export function clickToCommand(input: {
  eventId: string;
  title?: string;
  extendedProps: Record<string, unknown>;
}): OpenCommand | null {
  if (isGoogleOverlay(input.extendedProps)) return null;
  const props = readProps(input.extendedProps);
  if (props === null) {
    const parsed = parseOccurrenceId(input.eventId);
    if (parsed.deliverableId === "") return null;
    return {
      kind: "open",
      deliverableId: parsed.deliverableId,
      occurrenceStart: parsed.occurrenceStart.toISOString(),
      title: input.title,
    };
  }

  return {
    kind: "open",
    deliverableId: props.deliverableId,
    occurrenceStart: props.occurrenceStart,
    title: input.title,
  };
}

export function contextToCommand(input: {
  eventId: string;
  title: string;
  start: Date | null;
  end: Date | null;
  allDay: boolean;
  backgroundColor: string;
  extendedProps: Record<string, unknown>;
  x: number;
  y: number;
  timezone: string;
}): Extract<CalendarCommand, { kind: "context" }> | null {
  if (isGoogleOverlay(input.extendedProps)) return null;
  const props = readProps(input.extendedProps);
  const zone = props?.timezone ?? input.timezone;
  const start = input.start ?? new Date();
  const end = input.end ?? start;
  const startsAt =
    input.start === null ? start.toISOString() : fullCalendarMarkerToIso(start, zone);
  const endsAt = input.end === null ? startsAt : fullCalendarMarkerToIso(end, zone);
  if (props === null) {
    const parsed = parseOccurrenceId(input.eventId);
    if (parsed.deliverableId === "") return null;
    return {
      kind: "context",
      deliverableId: parsed.deliverableId,
      occurrenceStart: parsed.occurrenceStart.toISOString(),
      title: input.title,
      version: 1,
      typeId: "",
      color: input.backgroundColor,
      startsAt,
      endsAt,
      allDay: input.allDay,
      x: input.x,
      y: input.y,
    };
  }
  return {
    kind: "context",
    deliverableId: props.deliverableId,
    occurrenceStart: props.occurrenceStart,
    title: input.title,
    version: props.version,
    typeId: props.typeId,
    color: input.backgroundColor,
    startsAt,
    endsAt,
    allDay: input.allDay,
    x: input.x,
    y: input.y,
  };
}

export function selectToCommand(input: {
  start: Date;
  end: Date;
  allDay: boolean;
  timezone: string;
}): CalendarCommand {
  return {
    kind: "createRange",
    startsAt: fullCalendarMarkerToIso(input.start, input.timezone),
    endsAt: fullCalendarMarkerToIso(input.end, input.timezone),
    allDay: input.allDay,
  };
}

/** Nome da view do Dream Calendar → view do FullCalendar. */
export const VIEW_MAP = {
  mes: "dayGridMonth",
  semana: "timeGridWeek",
  dia: "timeGridDay",
  agenda: "listMonth",
  // `linha` não tem equivalente gratuito: `resourceTimeline` é FullCalendar
  // Premium. A timeline operacional é uma view própria, fora do plugin.
  linha: null,
} as const;

export function toFullCalendarView(view: keyof typeof VIEW_MAP): string | null {
  return VIEW_MAP[view];
}

export function fromFullCalendarView(viewType: string): keyof typeof VIEW_MAP {
  for (const [dream, fc] of Object.entries(VIEW_MAP)) {
    if (fc === viewType) return dream as keyof typeof VIEW_MAP;
  }
  return "mes";
}
